import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaBox, FaCreditCard, FaHistory, FaShippingFast } from 'react-icons/fa';
import SummaryApi from '../common';
import Pagination from '../components/Pagination';
import displayNPRCurrency from '../helpers/displayCurrency';
import { request } from '../helpers/request';


const AllOrder = () => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        pages: 1,
        limit: 20
    });

    const fetchOrderDetails = async (page = 1) => {
        setLoading(true)
        try {
            const response = await request({
                ...SummaryApi.allOrder,
                url: `${SummaryApi.allOrder.url}?page=${page}&limit=20`
            })
            const responseData = await response.json()
            if (responseData.success) {
                setData(responseData?.data || [])
                setPagination(responseData?.pagination)
            }
        } catch (error) {
            console.error("Error fetching orders:", error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchOrderDetails()
    }, [])

    const handlePageChange = (newPage) => {
        fetchOrderDetails(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className='space-y-6 md:space-y-8 pb-12'>
            {/* Header */}
            <div className='flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-6'>
                <div className='space-y-1 md:space-y-2'>
                    <div className='flex items-center gap-2 md:gap-3 text-accent text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-1'>
                        <FaHistory size={10} className="md:size-[12px]" />
                        <span>Sales Overview</span>
                    </div>
                    <h1 className='text-3xl md:text-5xl font-black text-primary tracking-tight'>Order History</h1>
                    <p className='text-xs md:text-base text-gray-500 font-medium'>Review and manage all customer transactions</p>
                </div>
            </div>

            {/* Orders List */}
            <div className='space-y-6 md:space-y-8'>
                {loading ? (
                    [1, 2, 3].map(i => (
                        <div key={i} className='h-64 bg-white rounded-[2rem] md:rounded-[2.5rem] border border-surface-border animate-pulse' />
                    ))
                ) : (data && data.length > 0) ? (
                    data.map((item, index) => (
                        <div key={item?._id || (item.userId + index)} className='bg-white rounded-[2rem] md:rounded-[2.5rem] border border-surface-border shadow-premium overflow-hidden group hover:border-accent/30 transition-all duration-500'>
                            {/* Order Header */}
                            <div className='bg-surface-muted/30 px-6 py-4 md:px-8 md:py-6 border-b border-surface-border flex flex-wrap items-center justify-between gap-4'>
                                <div className='flex items-center gap-4 md:gap-8'>
                                    <div>
                                        <p className='text-[8px] md:text-[10px] font-black uppercase tracking-widest text-gray-400 mb-0.5 md:mb-1'>Order Date</p>
                                        <p className='text-xs md:text-sm font-bold text-primary'>{moment(item?.createdAt).format('LL')}</p>
                                    </div>
                                    <div className='h-8 w-px bg-surface-border hidden sm:block' />
                                    <div>
                                        <p className='text-[8px] md:text-[10px] font-black uppercase tracking-widest text-gray-400 mb-0.5 md:mb-1'>User ID</p>
                                        <p className='font-mono font-bold text-primary text-[10px] md:text-sm'>#{item?.userId?.slice(-8).toUpperCase()}</p>
                                    </div>
                                </div>
                                <div className='flex items-center gap-3'>
                                    <span className={`px-3 py-1 text-[8px] md:text-[10px] font-black uppercase tracking-widest rounded-full border ${item?.paymentDetails?.payment_status === 'paid' ? 'bg-green-100 text-green-600 border-green-200' : 'bg-yellow-100 text-yellow-600 border-yellow-200'}`}>
                                        {item?.paymentDetails?.payment_status}
                                    </span>
                                </div>
                            </div>

                            <div className='p-5 md:p-8'>
                                 <div className='grid grid-cols-1 gap-6 md:gap-8'>
                                     {/* Products Column */}
                                     <div className='space-y-4'>
                                         <div className='flex items-center gap-2 text-primary font-black uppercase text-[10px] tracking-widest mb-1'>
                                             <FaBox className='text-accent' size={12} />
                                             <span>Purchased Items</span>
                                         </div>
                                         <div className='grid gap-3'>
                                             {item?.productDetails?.map((product, pIndex) => (
                                                 <div key={product?.productId + pIndex} className='flex items-center gap-4 p-3 md:p-4 bg-surface-muted/30 rounded-xl md:rounded-2xl border border-surface-border/50 hover:bg-white transition-colors duration-300'>
                                                     <div className='w-12 h-12 md:w-16 md:h-16 bg-white rounded-lg md:rounded-xl overflow-hidden p-1.5 flex-shrink-0 border border-surface-border shadow-sm'>
                                                         <img src={product?.image?.[0]} className='w-full h-full object-contain mix-blend-multiply' alt={product?.name} />
                                                     </div>
                                                     <div className='flex-1 min-w-0'>
                                                         <h4 className='font-bold text-primary text-xs md:text-base truncate'>{product?.name}</h4>
                                                         <div className='flex items-center gap-3 mt-0.5 text-[10px] md:text-sm'>
                                                             <p className='font-black text-gray-400'>{displayNPRCurrency(product?.price)}</p>
                                                             <p className='text-gray-400 font-medium'>Qty: <span className='text-primary font-bold'>{product?.quantity}</span></p>
                                                         </div>
                                                     </div>
                                                 </div>
                                             ))}
                                         </div>
                                     </div>
 
                                     {/* Order Meta Data */}
                                     <div className='grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-8 pt-6 border-t border-surface-border'>
                                         <div className='space-y-1.5'>
                                             <div className='flex items-center gap-1.5 text-gray-400 font-black uppercase text-[8px] md:text-[10px] tracking-widest'>
                                                 <FaCreditCard size={10} />
                                                 <span>Payment</span>
                                             </div>
                                             <p className='font-bold text-primary capitalize text-xs md:text-sm'>{item?.paymentDetails?.payment_method_type?.[0]?.replace('_', ' ')}</p>
                                         </div>
 
                                         <div className='space-y-1.5'>
                                             <div className='flex items-center gap-1.5 text-gray-400 font-black uppercase text-[8px] md:text-[10px] tracking-widest'>
                                                 <FaShippingFast size={10} />
                                                 <span>Shipping</span>
                                             </div>
                                             {item?.shipping_options?.map((shipping, sIndex) => (
                                                 <p key={sIndex} className='font-bold text-primary text-xs md:text-sm'>{displayNPRCurrency(shipping?.shipping_amount)}</p>
                                             ))}
                                         </div>
 
                                         <div className='col-span-2 md:col-span-1 bg-primary/5 rounded-xl md:rounded-2xl p-4 flex flex-col items-center justify-center border border-primary/10'>
                                             <p className='text-[8px] md:text-[10px] font-black uppercase tracking-widest text-primary/60 mb-0.5 md:mb-1'>Total Amount</p>
                                             <p className='text-xl md:text-2xl font-black text-primary'>{displayNPRCurrency(item?.totalAmount)}</p>
                                         </div>
                                     </div>
                                 </div>
                             </div>
                        </div>
                    ))
                ) : (
                    <div className='h-64 flex flex-col items-center justify-center bg-white rounded-[2rem] border border-surface-border text-center p-8 shadow-premium'>
                         <h3 className='text-xl font-black text-primary mb-2'>No Orders Yet</h3>
                         <p className='text-xs text-gray-500'>When customers make a purchase, they will appear here.</p>
                    </div>
                )}
            </div>

            <Pagination 
                page={pagination.page} 
                pages={pagination.pages} 
                onPageChange={handlePageChange} 
            />
        </div>
    )
}

export default AllOrder
